import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap, type ClassInfo } from 'lit/directives/class-map.js';
import { t } from './../common/localizer.ts';

type Controller =
    | 'Get'
    | 'GetKommissionen'
    | 'GetAnbieterverzeichnis'
    | 'GetSVGWMembers'
    | 'GetEhrenmitglieder'
    | 'GetNetzbetreiber';

type LabelKey =
    | 'Suchen' | 'Name' | 'Vorname' | 'Firma' | 'Strasse' | 'PLZ' | 'Ort'
    | 'ZertNr' | 'ZertGueltig' | 'Telefon' | 'EMail' | 'Url' | 'Stichwort'
    | 'NetzNr' | 'FernwaermeNetzNr' | 'FernKaelteNetzNr';

type Labels = Record<LabelKey, string>;

type SortDir = 'asc' | 'desc';

interface DatalistResponse {
    elements: Array<Record<string, unknown>>;
    totalCount: number;
}

const DEFAULT_LABELS: Labels = {
    Suchen: 'Global.Search',
    Name: 'Profile.Surname',
    Vorname: 'Profile.Prename',
    Firma: 'Profile.Company',
    Strasse: 'Profile.Street',
    PLZ: 'Profile.ZIP',
    Ort: 'Profile.Place',
    ZertNr: 'Datalist.CertNr',
    ZertGueltig: 'Datalist.CertValidUntil',
    Telefon: 'Profile.Telephone',
    EMail: 'Profile.E-Mail',
    Url: 'Profile.Url',
    Stichwort: 'Datalist.Keyword',
    NetzNr: 'Datalist.NetNr',
    FernwaermeNetzNr: 'Datalist.DistrictHeatingNetNr',
    FernKaelteNetzNr: 'Datalist.DistrictCoolingNetNr'
};

// Data fields returned per controller (used to read row values).
const BASE_FIELDS: Record<Controller, string[]> = {
    Get: ['Surname', 'Prename', 'Company', 'Street', 'Zip', 'City', 'ZertNr', 'ZertValid'],
    GetKommissionen: ['Surname', 'Prename', 'Company', 'Street', 'Zip', 'City', 'Email'],
    GetAnbieterverzeichnis: ['Company', 'Street', 'Zip', 'City', 'Tel', 'Email', 'url', 'Stichwort'],
    GetSVGWMembers: ['Company', 'Street', 'Zip', 'City'],
    GetEhrenmitglieder: ['Surname', 'Prename', 'Company', 'Street', 'Zip', 'City', 'Email'],
    GetNetzbetreiber: ['Company', 'Street', 'Zip', 'City']
};

// Header label keys, in the same order as the fields above.
const HEADER_KEYS: Record<Controller, LabelKey[]> = {
    Get: ['Name', 'Vorname', 'Firma', 'Strasse', 'PLZ', 'Ort', 'ZertNr', 'ZertGueltig'],
    GetAnbieterverzeichnis: ['Firma', 'Strasse', 'PLZ', 'Ort', 'Telefon', 'EMail', 'Url', 'Stichwort'],
    GetKommissionen: ['Name', 'Vorname', 'Firma', 'Strasse', 'PLZ', 'Ort', 'EMail'],
    GetSVGWMembers: ['Firma', 'Strasse', 'PLZ', 'Ort'],
    GetEhrenmitglieder: ['Name', 'Vorname', 'Firma', 'Strasse', 'PLZ', 'Ort', 'EMail'],
    GetNetzbetreiber: ['Firma', 'Strasse', 'PLZ', 'Ort'] // + dynamic NetzNr columns, see headerColumns
};

@customElement('datalist-px')
export class DatalistPx extends LitElement {
    @property({ type: String }) controller: Controller | '' = '';
    @property({ type: String }) parameter = '';
    @property({ type: String }) parameter2 = '';
    @property({ type: Number }) itemsperpage = 10;
    @property({ type: String }) language = '';
    @property({ type: Object }) labels: Partial<Labels> = {};

    @state() private datalist: DatalistResponse['elements'] = [];
    @state() private pager = 0;
    @state() private totalCount = 0;
    @state() private totalPages = 1;
    @state() private search = '';
    @state() private order: { field: string; type: SortDir } = { field: '', type: 'asc' };

    // Render into the light DOM so global Bootstrap / FontAwesome styles still apply.
    protected createRenderRoot(): HTMLElement {
        return this;
    }

    connectedCallback(): void {
        super.connectedCallback();
        void this.loadDatalist();
    }

    private get resolvedLabels(): Labels {
        return { ...DEFAULT_LABELS, ...this.labels };
    }

    // Fields to read from each row. Netzbetreiber gains dynamic NetzNr fields (as in beforeMount).
    private get availableFields(): string[] {
        if (this.controller === '') {
            return [];
        }
        if (this.controller !== 'GetNetzbetreiber') {
            return BASE_FIELDS[this.controller];
        }
        const fields = [...BASE_FIELDS.GetNetzbetreiber];
        fields.push(`${this.parameter}NetzNr`);
        if (this.parameter2) {
            fields.push(`${this.parameter2}NetzNr`);
        }
        return fields;
    }

    // Header labels, aligned by index with availableFields.
    private get headerColumns(): string[] {
        if (this.controller === '') {
            return [];
        }
        const labels = this.resolvedLabels;
        const keys = [...HEADER_KEYS[this.controller]];
        if (this.controller === 'GetNetzbetreiber') {
            if (this.parameter2) {
                keys.push('FernwaermeNetzNr', 'FernKaelteNetzNr');
            } else {
                keys.push('NetzNr');
            }
        }
        return keys.map((k) => labels[k] ?? k);
    }

    private previousPage = (): void => {
        if (this.pager > 0) {
            this.pager--;
            void this.loadDatalist();
        }
    };

    private nextPage = (): void => {
        this.pager++;
        void this.loadDatalist();
    };

    private goToPage(page: number): void {
        this.pager = page;
        void this.loadDatalist();
    }

    private submitSearch = (e: Event): void => {
        e.preventDefault();
        this.pager = 0;
        void this.loadDatalist();
    };

    private orderFieldClass(fieldIndex: number): ClassInfo {
        const field = this.availableFields[fieldIndex];
        return {
            asc: this.order.type === 'asc' && this.order.field === field,
            desc: this.order.type === 'desc' && this.order.field === field,
            active: this.order.field === field
        };
    }

    private sortIcon(fieldIndex: number): TemplateResult {
        const field = this.availableFields[fieldIndex];
        if (this.order.field !== field) {
            return html`<i class="fal fa-sort"></i>`;
        }
        return this.order.type === 'asc'
            ? html`<i class="fal fa-caret-up"></i>`
            : html`<i class="fal fa-caret-down"></i>`;
    }

    private orderDatalist(fieldIndex: number): void {
        const field = this.availableFields[fieldIndex];

        // Szenario 1: same field -> toggle direction
        if (this.order.field === field) {
            this.order = {
                field,
                type: this.order.type === 'asc' ? 'desc' : 'asc'
            };
        }
        // Szenario 2: new field -> keep direction, but reset a lingering 'desc' to 'asc'
        else {
            this.order = {
                field,
                type: this.order.type === 'desc' ? 'asc' : this.order.type
            };
        }

        void this.loadDatalist();
    }

    private async loadDatalist(): Promise<void> {
        if (this.controller === '') {
            return;
        }

        this.datalist = [];

        const order = this.order.field === '' ? '' : `${this.order.field}_${this.order.type}`;

        const params = new URLSearchParams({
            Pager: String(this.pager),
            ItemsPerPage: String(this.itemsperpage),
            Search: this.search,
            Order: order
        });

        switch (this.controller) {
            case 'Get':
            case 'GetSVGWMembers':
                params.set('VertragDefID', this.parameter);
                break;
            case 'GetKommissionen':
                params.set('GroupID', this.parameter);
                break;
            case 'GetNetzbetreiber':
                params.set('type', this.parameter);
                break;
            // GetAnbieterverzeichnis / GetEhrenmitglieder: no extra parameter
        }

        const url = `/umbraco/md/Datalist/${this.controller}?${params.toString()}`;

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Request failed: ${response.status}`);
            }
            const data = (await response.json()) as DatalistResponse | null;
            if (data) {
                this.datalist = data.elements;
                this.totalCount = data.totalCount;
                const perPage = Number(this.itemsperpage);
                this.totalPages =
                    this.totalCount % perPage === 0
                        ? this.totalCount / perPage
                        : Math.floor(this.totalCount / perPage) + 1;
            }
        } catch {}
    }

    protected render(): TemplateResult {
        const labels = this.resolvedLabels;
        const columns = this.headerColumns;
        const fields = this.availableFields;
        const perPage = Number(this.itemsperpage);

        return html`
      <div>
        <form class="d-flex align-items-center flex-wrap gap-2" @submit=${this.submitSearch}>
          <div class="mb-2">
            <input
              type="text"
              name="search"
              class="form-control-plaintext border"
              .value=${this.search}
              @input=${(e: Event) => {
            this.search = (e.target as HTMLInputElement).value;
        }} />
          </div>
          <div class="mx-sm-3 mb-2">
            <button type="submit" class="btn">${labels.Suchen}</button>
          </div>
        </form>

        <div class="table-responsive">
          <table class="table">
            <tr>
              ${columns.map(
            (label, i) => html`
                <th>
                    <span
                        class=${classMap({ orderColHead: true, ...this.orderFieldClass(i) })}
                        @click=${() => this.orderDatalist(i)}>${t(label)}${this.sortIcon(i)}</span>
                </th>
                `
        )}
            </tr>
            ${this.datalist.map(
            (row) => html`
                <tr>
                  ${fields.map((field) => html`<td>${row[field] as unknown}</td>`)}
                </tr>
              `
        )}
          </table>
        </div>

        ${this.totalCount > perPage
            ? html`
              <nav aria-label="Page navigation">
                <ul class="pagination paging">
                  ${this.pager !== 0
                ? html`<li class="md-paging-previous"><a @click=${this.previousPage}>&lt;</a></li>`
                : ''}
                  ${Array.from({ length: this.totalPages }, (_, k) => k + 1).map(
                (pgnr) => html`
                      <li class=${classMap({ 'md-paging-page': true, active: this.pager + 1 === pgnr })}>
                        <a @click=${() => this.goToPage(pgnr - 1)}>${pgnr}</a>
                      </li>
                    `
                )}
                  ${this.pager + 1 !== this.totalPages
                ? html`<li class="md-paging-next"><a @click=${this.nextPage}>&gt;</a></li>`
                : ''}
                </ul>
              </nav>
            `
            : ''}
      </div>
    `;
    }
}

// Global from the existing site (previously called directly in the Vue catch handler).
declare global {
    interface HTMLElementTagNameMap {
        'datalist-px': DatalistPx;
    }
}
